import _ from 'lodash';
import { ApiError, NetworkError } from '../types/errors';
import localization from '../common/localization';
import { FitMode } from 'react-native-idc-player';

const emptyObject = {};
const emptyArray: any[] = [];

function chunk<T>(array: T[], size = 1): T[][] {
  size = Math.max(size, 0);
  const length = array == null ? 0 : array.length;
  if (!length || size < 1) {
    return [];
  }
  let index = 0;
  let resIndex = 0;
  const result = new Array(Math.ceil(length / size));

  while (index < length) {
    result[resIndex++] = array.slice(index, (index += size));
  }
  return result;
}

function secondsToHmLabel(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}:${m >= 10 ? m : `0${m}`}`;
}

function secondsToHmsLabel(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}:${m >= 10 ? m : `0${m}`}:${s >= 10 ? s : `0${s}`}`;
}

function secondsToHmParts(seconds: number): [number, number] {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return [h, m];
}

function secondsToMsLabel(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m >= 10 ? m : `0${m}`}:${s >= 10 ? s : `0${s}`}`;
}

function mod(a: number, b: number) {
  const res = a % b;
  if (res >= 0) return res;
  return res + b;
}

function reduceToIndex<T1, T2>(
  array: T1[],
  callbackfn: (
    previousValue: T2,
    currentValue: T1,
    currentIndex: number,
    array: T1[],
  ) => T2,
  initialValue: T2,
  toIndex: number,
) {
  for (let i = 0; i < array.length && i < toIndex; i++)
    initialValue = callbackfn(initialValue, array[i], i, array);

  return initialValue;
}

function findIndex<T>(
  array: T[],
  predicate: (value: T) => boolean,
  start = 0,
  end: number | undefined = undefined,
) {
  end ??= array.length;
  for (let i = start; i < end; i++) {
    const e = array[i];
    if (predicate(e)) return i;
  }
  return -1;
}

function shallowCompare(a: any, b: any): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

function idIntersection<T extends { id: number }>(items: T[], ids: number[]) {
  return _.intersectionBy(items, ids, o => (o instanceof Object ? o.id : o));
}

function getErrorMessage(error: any): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof NetworkError) return localization.error.networkError;
  if (error instanceof Object && 'message' in error) return error.message;
  return localization.error.code0;
}

function getTvErrorMessage(error: any): string {
  if (error instanceof ApiError) return localization.errCodeToMsg(error.code);
  else if (error instanceof NetworkError)
    return localization.error.networkError;
  else return localization.error.code0;
}

function getFitModeMessage(mode: FitMode) {
  switch (mode) {
    case 'fit':
      return localization.content.fitMode;
    case 'fill':
      return localization.content.fillMode;
    case 'cover':
      return localization.content.coverMode;
    default:
      return localization.content.zoomMode;
  }
}

const Utils = {
  emptyObject,
  emptyArray,
  chunk,
  secondsToHmLabel,
  secondsToHmsLabel,
  secondsToHmParts,
  secondsToMsLabel,
  mod,
  reduceToIndex,
  findIndex,
  shallowCompare,
  idIntersection,
  getErrorMessage,
  getTvErrorMessage,
  getFitModeMessage,
};

export default Utils;
